import { useEffect } from 'react';
import { useMutation } from '@apollo/client';
import { useTranslation } from 'react-i18next';
import { makeUrlWithLang, prettyNumber } from '../utils/helpers';
import config from '../config';
import { sendAnalyticsDataMutation } from '../api/mutations';
import {
  CompanyTargetTypeEnum,
  AnalyticLogEventTypeEnum,
  AnalyticLogTargetTypeEnum,
  ItemStatusEnum,
  ItemUserActionsEnum
} from '../graphql/types';
import {
  CreateAnalyticLogMutation,
  CurrentItemQuery
} from '../graphql/graphql';
import { useAnalyticLogSource } from './useAnalyticLogSource';
import useScreenSize from './useScreenSize';

export const useCurrentItemData = (
  currentItemData: CurrentItemQuery['item'],
  asPath: string,
  isSentHitEvent: boolean
) => {
  const { protocol, host } = config;
  const { isSmall } = useScreenSize();
  const {
    t,
    i18n: { language: lang }
  } = useTranslation();
  const analyticLogSource = useAnalyticLogSource();

  const hostString = (path: string) =>
    makeUrlWithLang({ protocol, host, path, lang });

  const [sendAnalyticsData] = useMutation<CreateAnalyticLogMutation>(
    sendAnalyticsDataMutation,
    {
      variables: {
        targetId: currentItemData?.id,
        targetType: AnalyticLogTargetTypeEnum.Item,
        sourceLink: hostString(asPath),
        eventType: AnalyticLogEventTypeEnum.ItemHit,
        source: analyticLogSource
      },
      ssr: false
    }
  );
  const item = currentItemData;
  const maskedPhonesArray = item?.phones?.map(i => i.value);
  const maskedPhones = item?.phones ? maskedPhonesArray : [];
  const itemPath = item?.path;
  const prettyPrice = prettyNumber(item?.price?.total);
  const prettyAreaRoundedUp = Number.isInteger(item?.area?.value)
    ? item?.area?.value
    : Number(item?.area?.value).toFixed(1);
  const prettyArea = prettyNumber(prettyAreaRoundedUp);
  const prettyAreaWithUnits = item?.area
    ? `${prettyArea} ${item?.area?.units}`
    : '';
  const prettyLandArea = prettyNumber(item?.landArea?.value);
  const prettyLandAreaWithUnits = item?.landArea
    ? `${prettyLandArea} ${item?.landArea?.units}`
    : '';
  const showElectronicBanners = ['1', '2', '3', '5'].includes(
    item?.category?.id || ''
  );
  const isEditAvailable = !!item?.actions?.includes(ItemUserActionsEnum.Edit);
  const isRemoveAvailable = !!item?.actions?.includes(
    ItemUserActionsEnum.Remove
  );

  const isAuthor = !!item?.isMine;
  const isResidence =
    item?.company?.targetType === CompanyTargetTypeEnum.Residence &&
    !!item?.business;
  const isAgency =
    item?.company?.targetType === CompanyTargetTypeEnum.Agency &&
    !!item?.business;
  const isPublished = item?.state === ItemStatusEnum.Published;
  const isLimited = item?.state === ItemStatusEnum.AwaitingPayment;
  const isExpired = item?.state === ItemStatusEnum.Expired;
  const isRejected = item?.state === ItemStatusEnum.Rejected;
  const isPending = item?.state === ItemStatusEnum.Pending;
  const isValidated = item?.state === ItemStatusEnum.Validated;
  const isLeased = !!item?.leased;
  const isExpiredManually = !!(isExpired && item?.isExpiredManually);
  const paidServices = item?.paidServices || [];
  const isExternalUser = !isAuthor ?? (!isAgency && !isResidence);
  const phonesSectionNoBottomSpace = !item?.business
    ? !!item?.business
    : isExternalUser;
  const getPriceCurrency = () => {
    if (item?.paidDaily === null || !isLeased) {
      return item?.price?.currency;
    }

    return item?.paidDaily
      ? `${item?.price?.currency}/${t('cards.item_card.per_day')}`
      : `${item?.price?.currency}/${t('cards.item_card.per_month')}`;
  };

  useEffect(() => {
    try {
      // Log ONLY real user views on mobile devices.
      // Running this on the client helps filter out bots/crawlers that don't execute JS,
      // ensuring mobile analytics data is cleaner.
      if (currentItemData?.isMine === false && isSmall && !isSentHitEvent) {
        sendAnalyticsData();
      }
    } catch (e) {
      console.log(e);
    }
  }, [currentItemData?.isMine]);

  return {
    item,
    maskedPhones,
    isEditAvailable,
    isRemoveAvailable,
    isAuthor,
    isResidence,
    isAgency,
    isPublished,
    isLimited,
    isExpired,
    isRejected,
    isPending,
    isLeased,
    isExpiredManually,
    paidServices,
    itemPath,
    prettyPrice,
    prettyArea,
    prettyAreaWithUnits,
    prettyLandAreaWithUnits,
    showElectronicBanners,
    phonesSectionNoBottomSpace,
    getPriceCurrency,
    isValidated
  };
};
